import { FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorAlert from '@/shared/components/ErrorAlert';
import FormField from '@/shared/components/FormField';
import FormInput from '@/shared/components/FormInput';
import Button from '@/shared/components/Button';

interface Props {
  password: string;
  confirmPassword: string;
  error: string | null;
  isLoading: boolean;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onBackToSignIn: () => void;
}

export default function PasswordResetForm({
  password,
  confirmPassword,
  error,
  isLoading,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onBackToSignIn,
}: Props) {
  const intl = useIntl();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          <FormattedMessage id="auth.passwordReset.greeting" />
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          <FormattedMessage id="auth.passwordReset.heading" />
        </h2>
      </div>

      {error && <ErrorAlert message={error} />}

      <form className="space-y-5" onSubmit={onSubmit}>
        <FormField id="password" labelId="auth.passwordReset.newPasswordLabel">
          <FormInput
            id="password"
            type="password"
            autoComplete="new-password"
            required
            placeholder={intl.formatMessage({ id: 'auth.passwordReset.newPasswordPlaceholder' })}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
          />
        </FormField>

        <FormField id="confirmPassword" labelId="auth.passwordReset.confirmPasswordLabel">
          <FormInput
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            placeholder={intl.formatMessage({ id: 'auth.passwordReset.confirmPasswordPlaceholder' })}
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
          />
        </FormField>

        <Button type="submit" disabled={isLoading} fullWidth>
          {isLoading ? (
            <FormattedMessage id="auth.passwordReset.resetSubmitting" />
          ) : (
            <FormattedMessage id="auth.passwordReset.resetSubmit" />
          )}
        </Button>
      </form>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToSignIn}
          className="text-sm font-semibold text-brand-700 hover:text-brand-600"
        >
          <FormattedMessage id="auth.passwordReset.backToSignIn" />
        </button>
      </div>
    </div>
  );
}
